import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import * as yup from "yup";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ActionButton from "../../components/ActionButton";
import DeleteStaffWorkPatternModal from "../../components/DeleteStaffWorkPatternModal";
import { useTenant } from "../../context/TenantContext";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { READ_STAFF_WORK_PATTERNS } from "../../graph/staffWorkPatterns/queries";
import {
  DELETE_STAFF_WORK_PATTERN,
  UPDATE_STAFF_WORK_PATTERN,
} from "../../graph/staffWorkPatterns/mutations.js";

const client = new GraphQLClient(graphqlEndpoint);

const daysOfWeek = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

const validationSchema = yup.object({
  name: yup.string().trim().required("Pattern name is required"),
  days: yup
    .array()
    .of(
      yup.object({
        dayOfWeek: yup.string().required(),
        isWorkingDay: yup.boolean().required(),
        startTime: yup.string().when("isWorkingDay", {
          is: true,
          then: (schema) => schema.required("Start time is required"),
          otherwise: (schema) => schema.nullable(),
        }),
        endTime: yup.string().when("isWorkingDay", {
          is: true,
          then: (schema) => schema.required("End time is required"),
          otherwise: (schema) => schema.nullable(),
        }),
        unpaidBreakMinutes: yup.number().min(0).required(),
      })
    )
    .length(7),
});

const mapPatternDays = (days = []) =>
  daysOfWeek.map((dayOfWeek) => {
    const existingDay = days.find((day) => day.dayOfWeek === dayOfWeek);

    return {
      dayOfWeek,
      isWorkingDay: Boolean(existingDay?.isWorkingDay),
      startTime: existingDay?.startTime || "",
      endTime: existingDay?.endTime || "",
      unpaidBreakMinutes: existingDay?.unpaidBreakMinutes || 0,
    };
  });

const formatDayLabel = (day) =>
  day.charAt(0) + day.slice(1).toLowerCase();

const EditStaffWorkPatternForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useTenant();
  const { setSnackbar } = useSnackbar();

  const staffTenantUserID = location.state?.staffTenantUserID || id;

  const searchParams = new URLSearchParams(location.search);
  const leaveYearID =
    location.state?.leaveYearID || searchParams.get("leaveYearID") || "";
  const staffName =
    location.state?.staffName || staffTenantUserID || "selected staff member";
  const redirectPath =
    location.state?.redirectPath || "/manageStaffWorkPatterns";

  const [pattern, setPattern] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    const fetchPattern = async () => {
      if (!tenantId || !staffTenantUserID || !leaveYearID) {
        setLoading(false);
        setNotFound(true);
        return;
      }

      try {
        const data = await client.request(READ_STAFF_WORK_PATTERNS, {
          tenantID: tenantId,
          staffTenantUserID,
          leaveYearID,
        });

        const patterns = data.readStaffWorkPatterns || [];

        if (patterns.length > 1) {
          throw new Error(
            "More than one work pattern exists for this staff member and leave year"
          );
        }

        setPattern(patterns[0] || null);
        setNotFound(patterns.length === 0);
      } catch (err) {
        console.error("Failed to load staff work pattern:", err);
        setNotFound(true);
        setSnackbar({
          open: true,
          severity: "error",
          message: "Failed to load staff work pattern",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPattern();
  }, [tenantId, staffTenantUserID, leaveYearID, setSnackbar]);

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!pattern?.id) return;

    const input = {
      id: pattern.id,
      name: values.name.trim(),
      days: values.days.map((day) => ({
        dayOfWeek: day.dayOfWeek,
        isWorkingDay: day.isWorkingDay,
        startTime: day.isWorkingDay ? day.startTime : null,
        endTime: day.isWorkingDay ? day.endTime : null,
        unpaidBreakMinutes: day.isWorkingDay
          ? Number(day.unpaidBreakMinutes)
          : 0,
      })),
    };

    try {
      await client.request(UPDATE_STAFF_WORK_PATTERN, { input });

      setSnackbar({
        open: true,
        severity: "success",
        message: "Staff work pattern updated successfully",
      });

      navigate(redirectPath);
    } catch (err) {
      console.error("Failed to update staff work pattern:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to update staff work pattern",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!pattern?.id) return;

    try {
      const result = await client.request(DELETE_STAFF_WORK_PATTERN, {
        id: pattern.id,
      });

      if (!result.deleteStaffWorkPattern) {
        throw new Error("Staff work pattern was not deleted");
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: "Staff work pattern deleted successfully",
      });

      setDeleteModalOpen(false);
      navigate(redirectPath);
    } catch (err) {
      console.error("Failed to delete staff work pattern:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete staff work pattern",
      });
    }
  };

  if (loading) {
    return <Typography>Loading work pattern...</Typography>;
  }

  if (!tenantId || !leaveYearID) {
    return (
      <Box>
        <Header
          title="STAFF WORK PATTERN"
          subtitle="Select a tenant and leave year to manage this pattern"
        />
        <Typography mt={3}>
          Return to the staff work patterns list and select a leave year.
        </Typography>
      </Box>
    );
  }

  if (notFound || !pattern) {
    return (
      <Box>
        <Header
          title="STAFF WORK PATTERN"
          subtitle={`No work pattern exists for ${staffName} in this leave year`}
        />

        <Box display="flex" justifyContent="flex-end" mt={3}>
          <ActionButton
            tone="gold"
            onClick={() =>
              navigate("/createStaffWorkPattern", {
                state: {
                  tenantID: tenantId,
                  staffTenantUserID,
                  staffName,
                  leaveYearID,
                  redirectPath,
                },
              })
            }
          >
            Create Work Pattern
          </ActionButton>
        </Box>
      </Box>
    );
  }

  const initialValues = {
    name: pattern.name || "",
    days: mapPatternDays(pattern.days),
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="EDIT STAFF WORK PATTERN"
          subtitle={`Edit work pattern for ${staffName}`}
        />

        <ActionButton
          tone="danger"
          size="small"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteModalOpen(true)}
        >
          Delete Work Pattern
        </ActionButton>
      </Box>

      <Typography mt={2} mb={3}>
        Effective {pattern.effectiveFrom}
        {pattern.effectiveTo ? ` to ${pattern.effectiveTo}` : ""}
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="20px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                label="Pattern Name"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 4" }}
              />

              {values.days.map((day, index) => (
                <Box
                  key={day.dayOfWeek}
                  sx={{
                    gridColumn: "span 2",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={day.isWorkingDay}
                        onChange={(event) => {
                          const isWorkingDay = event.target.checked;

                          setFieldValue(
                            `days.${index}.isWorkingDay`,
                            isWorkingDay
                          );

                          if (!isWorkingDay) {
                            setFieldValue(`days.${index}.startTime`, "");
                            setFieldValue(`days.${index}.endTime`, "");
                            setFieldValue(
                              `days.${index}.unpaidBreakMinutes`,
                              0
                            );
                          }
                        }}
                      />
                    }
                    label={formatDayLabel(day.dayOfWeek)}
                  />

                  {day.isWorkingDay && (
                    <Box display="grid" gap={2} mt={1}>
                      <TextField
                        label="Start Time"
                        name={`days.${index}.startTime`}
                        type="time"
                        value={day.startTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{ shrink: true }}
                        error={
                          !!touched.days?.[index]?.startTime &&
                          !!errors.days?.[index]?.startTime
                        }
                        helperText={
                          touched.days?.[index]?.startTime &&
                          errors.days?.[index]?.startTime
                        }
                      />

                      <TextField
                        label="End Time"
                        name={`days.${index}.endTime`}
                        type="time"
                        value={day.endTime}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        InputLabelProps={{ shrink: true }}
                        error={
                          !!touched.days?.[index]?.endTime &&
                          !!errors.days?.[index]?.endTime
                        }
                        helperText={
                          touched.days?.[index]?.endTime &&
                          errors.days?.[index]?.endTime
                        }
                      />

                      <TextField
                        label="Unpaid Break (minutes)"
                        name={`days.${index}.unpaidBreakMinutes`}
                        type="number"
                        value={day.unpaidBreakMinutes}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        inputProps={{ min: 0 }}
                        error={
                          !!touched.days?.[index]?.unpaidBreakMinutes &&
                          !!errors.days?.[index]?.unpaidBreakMinutes
                        }
                        helperText={
                          touched.days?.[index]?.unpaidBreakMinutes &&
                          !!errors.days?.[index]?.unpaidBreakMinutes
                        }
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <ActionButton
                type="submit"
                tone="success"
                disabled={isSubmitting}
              >
                Save Changes
              </ActionButton>
            </Box>
          </form>
        )}
      </Formik>

      <DeleteStaffWorkPatternModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        pattern={pattern}
        staffName={staffName}
      />
    </Box>
  );
};

export default EditStaffWorkPatternForm;